import {ControllerFlowAPI, IHttpClient} from '@wix/yoshi-flow-editor'
import {listReservationLocations} from '@wix/ambassador-table-reservations-v1-reservation-location/http'
import {
  CursorPaging,
  ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {getDataFromMemoryCache, setDataToMemoryCache} from '../utils/dataCapsule'
import {setSsrWarmupData, getSsrWarmupData} from '../utils/warmupData'
import {ClientWithErrorHandler} from '../utils/errors'

const CACHE_KEY = 'tr-reservationLocations'
const SSR_WARMUP_KEY = 'tr-reservationLocations-ssr-warmup'

const MAX_REQUESTS_NUMBER = 3
let cachedSettingsReservationLocations: ReservationLocation[] | undefined
let pendingSettingsReservationLocations: Promise<ReservationLocation[]> | undefined
let settingsReservationLocationsRequestId = 0

export const fetchPaginatedReservationLocations = async (
  httpClient: IHttpClient,
  withErrorHandler: ClientWithErrorHandler,
): Promise<ReservationLocation[]> => {
  let allReservationLocations: ReservationLocation[] = []
  let cursor: CursorPaging['cursor']
  let requestCount = 0
  let hasNextPage = true

  while (hasNextPage && requestCount < MAX_REQUESTS_NUMBER) {
    const response = await withErrorHandler(
      async () =>
        httpClient.request(
          listReservationLocations({
            paging: {
              limit: 100,
              cursor,
            },
          }),
        ),
      {
        errorCodesMap: {},
      },
    )

    if (!response?.data?.reservationLocations) {
      break
    }

    const currentBatchLocations = response.data.reservationLocations.filter(
      ({archived}) => !archived,
    )

    allReservationLocations = [...allReservationLocations, ...currentBatchLocations]

    cursor = response.data.pagingMetadata?.cursors?.next
    hasNextPage = Boolean(response.data.pagingMetadata?.hasNext)
    requestCount++
  }

  return allReservationLocations
}

export const getCachedReservationLocations = (
  httpClient: IHttpClient,
  withErrorHandler: ClientWithErrorHandler,
): Promise<ReservationLocation[]> => {
  if (cachedSettingsReservationLocations !== undefined) {
    return Promise.resolve(cachedSettingsReservationLocations)
  }

  if (!pendingSettingsReservationLocations) {
    pendingSettingsReservationLocations = fetchAndCacheReservationLocations(
      httpClient,
      withErrorHandler,
    )
  }

  return pendingSettingsReservationLocations
}

export const refreshCachedReservationLocations = (
  httpClient: IHttpClient,
  withErrorHandler: ClientWithErrorHandler,
): Promise<ReservationLocation[]> => {
  pendingSettingsReservationLocations = fetchAndCacheReservationLocations(
    httpClient,
    withErrorHandler,
  )

  return pendingSettingsReservationLocations
}

export const resetCachedReservationLocations = () => {
  cachedSettingsReservationLocations = undefined
  pendingSettingsReservationLocations = undefined
  settingsReservationLocationsRequestId = 0
}

const fetchAndCacheReservationLocations = (
  httpClient: IHttpClient,
  withErrorHandler: ClientWithErrorHandler,
) => {
  const requestId = ++settingsReservationLocationsRequestId

  return fetchPaginatedReservationLocations(httpClient, withErrorHandler)
    .then((reservationLocations) => {
      if (requestId === settingsReservationLocationsRequestId) {
        cachedSettingsReservationLocations = reservationLocations
      }

      return reservationLocations
    })
    .catch((e) => {
      if (requestId === settingsReservationLocationsRequestId) {
        pendingSettingsReservationLocations = undefined
      }

      throw e
    })
}

const getReservationLocations = async (
  flowAPI: ControllerFlowAPI,
): Promise<ReservationLocation[]> => {
  const cachedReservationLocations = getDataFromMemoryCache<ReservationLocation[]>(
    flowAPI,
    CACHE_KEY,
  )
  const ssrWarmupData = getSsrWarmupData<ReservationLocation[]>(flowAPI, SSR_WARMUP_KEY)

  const ignoreCache = !flowAPI.environment.isViewer

  // client cache
  if (cachedReservationLocations && !ignoreCache) {
    return cachedReservationLocations
    // ssr cache
  } else if (ssrWarmupData && !ignoreCache) {
    return ssrWarmupData
  } else {
    const allReservationLocations = await fetchPaginatedReservationLocations(
      flowAPI.httpClient,
      flowAPI.errorHandler.withErrorHandler,
    )

    setDataToMemoryCache(flowAPI, CACHE_KEY, allReservationLocations)
    setSsrWarmupData(flowAPI, SSR_WARMUP_KEY, allReservationLocations)

    return allReservationLocations
  }
}

export const reservationLocationsService = {
  getReservationLocations,
  getCachedReservationLocations,
  refreshCachedReservationLocations,
}
